import { useCallback, useEffect, useRef, useState } from "react";
import ClientDetailRepository, { ClientDetail } from "../../../Repositories/ClientDetailRepository";
import { BusinessError } from "../../../Objects/exceptions";

/**
 * 'denied' : erreur 4xx (client inexistant ou non rattaché au professeur).
 * 'failed' : erreur réseau ou serveur, on peut réessayer.
 */
export type ClientDetailError = "denied" | "failed" | null;

/**
 * Données de la fiche client, hors Redux : la liste « Mes élèves » reste
 * dans le store, la fiche garde son propre état.
 */
const useClientDetail = (teachrId: number | undefined, clientId: number) => {
    const [detail, setDetail] = useState<ClientDetail | null>(null);
    /** Ligne de la liste (all_clients) utilisée par le menu d'actions. */
    const [row, setRow] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<ClientDetailError>(null);
    // Ignore les réponses d'un chargement dépassé (changement de client, démontage)
    const requestId = useRef(0);

    const load = useCallback(async (silent: boolean) => {
        if (!teachrId) return;
        if (!Number.isInteger(clientId) || clientId <= 0) {
            setError("denied");
            setLoading(false);
            return;
        }
        const current = ++requestId.current;
        if (!silent) {
            setLoading(true);
            setError(null);
        }
        try {
            const response = await ClientDetailRepository.getClientDetail(teachrId, clientId, silent);
            if (current !== requestId.current) return;
            setDetail(response);
            setError(null);
            setLoading(false);

            // Le menu d'actions n'est pas indispensable à la lecture : son
            // échec ne bloque pas la fiche.
            try {
                const listRow = await ClientDetailRepository.getClientRow(
                    teachrId,
                    clientId,
                    response.client.mail,
                    response.fees?.isArchive ?? false,
                    silent
                );
                if (current === requestId.current) setRow(listRow);
            } catch {
                if (current === requestId.current) setRow(null);
            }
        } catch (e) {
            if (current !== requestId.current) return;
            // Un rechargement silencieux raté garde la fiche affichée
            if (!silent) {
                setDetail(null);
                setError(e instanceof BusinessError ? "denied" : "failed");
            }
            setLoading(false);
        }
    }, [teachrId, clientId]);

    useEffect(() => {
        load(false);
        return () => {
            requestId.current += 1;
        };
    }, [load]);

    /** Rechargement après une action : la fiche reste affichée pendant l'appel. */
    const refresh = useCallback(() => load(true), [load]);
    const retry = useCallback(() => load(false), [load]);

    return { detail, row, loading, error, refresh, retry };
};

export default useClientDetail;
